import React, { useEffect, useMemo, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import MenuOpenRounded from "@mui/icons-material/MenuOpenRounded";
import { LayoutGroup, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import NavGroupSection from "./NavGroup";
import QuickShortcuts from "./QuickShortcuts";
import { NavIcon } from "./navIcons";
import {
  NavGroup,
  NavPage,
  findActiveGroupId,
  searchPages,
  useNavModel,
} from "./navModel";
import {
  Kbd,
  NoMatch,
  PageLabel,
  SearchField,
  isPlainClick,
} from "./navPrimitives";
import { NAV, ghostButton, resetRow, rowSx } from "./navTheme";
import { MOD_K } from "../../../utils/platform";

interface SidebarProps {
  /** `mobile` enlarges hit areas and swaps collapse for close. */
  variant?: "desktop" | "mobile";
  onCollapse?: () => void;
  onNavigate?: () => void;
  onOpenPalette?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  variant = "desktop",
  onCollapse,
  onNavigate,
  onOpenPalette,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { groups, shortcuts, isLoading } = useNavModel();
  const touch = variant === "mobile";

  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [query, setQuery] = useState("");

  const activeGroupId = useMemo(
    () => findActiveGroupId(groups, pathname),
    [groups, pathname],
  );

  // The group owning the current route opens itself; hand-opened ones stay open.
  useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((prev) =>
      prev.includes(activeGroupId) ? prev : [...prev, activeGroupId],
    );
  }, [activeGroupId]);

  const results = useMemo(() => searchPages(query, groups), [query, groups]);
  const isSearching = query.trim().length > 0;

  const handleToggle = (groupId: string) =>
    setOpenGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );

  const handleNavigate = (page: NavPage) => {
    navigate(page.link);
    setQuery("");
    onNavigate?.();
  };

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: NAV.surface,
        borderRight: touch ? "none" : `1px solid ${NAV.border}`,
        color: NAV.textIdle,
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: 1.75,
          pr: 1,
          pt: touch ? 1 : 1.5,
          pb: 1.25,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => {
            navigate(
              shortcuts[0]?.link ?? groups[0]?.pages[0]?.link ?? pathname,
            );
            onNavigate?.();
          }}
          aria-label="Home"
          sx={{
            ...resetRow,
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            component="img"
            src="/SPARK LOGO-white.png"
            alt="Spark"
            sx={{
              height: touch ? 46 : 56,
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>

        <Box
          component={motion.button}
          type="button"
          whileTap={{ scale: 0.9 }}
          transition={NAV.spring}
          onClick={onCollapse}
          aria-label={touch ? "Close navigation" : "Collapse navigation"}
          sx={ghostButton(touch ? 44 : 36)}
        >
          {touch ? (
            <CloseRounded sx={{ fontSize: 24 }} />
          ) : (
            <MenuOpenRounded sx={{ fontSize: 21 }} />
          )}
        </Box>
      </Box>

      <Box sx={{ flex: "0 0 auto" }}>
        {isLoading ? (
          <Box
            sx={{ display: "flex", gap: 0.75, px: touch ? 1.5 : 1.75, pb: 1.5 }}
          >
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                width={touch ? 72 : 60}
                height={touch ? 64 : 56}
                sx={{ bgcolor: NAV.hover, borderRadius: "14px", flexShrink: 0 }}
              />
            ))}
          </Box>
        ) : (
          <QuickShortcuts
            shortcuts={shortcuts}
            groups={groups}
            touch={touch}
            onNavigate={onNavigate}
          />
        )}
      </Box>

      <Box sx={{ flex: "0 0 auto", px: touch ? 1.5 : 1.75, pb: 1.25 }}>
        <SearchField
          value={query}
          onChange={setQuery}
          onKeyDown={(event) => {
            if (event.key === "Escape") setQuery("");
            if (event.key === "Enter" && results[0]) handleNavigate(results[0]);
          }}
          placeholder="Search pages…"
          ariaLabel="Search pages"
          height={touch ? 46 : 40}
          fontSize={touch ? "0.9375rem" : "0.8125rem"}
          trailing={
            query ? (
              <Box
                component="button"
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                sx={{
                  ...resetRow,
                  display: "grid",
                  placeItems: "center",
                  color: NAV.textMuted,
                  flexShrink: 0,
                }}
              >
                <CloseRounded sx={{ fontSize: 17 }} />
              </Box>
            ) : (
              !touch && (
                <Box
                  component="button"
                  type="button"
                  onClick={onOpenPalette}
                  aria-label="Open command palette"
                  sx={{ ...resetRow, display: "flex" }}
                >
                  <Kbd>{MOD_K}</Kbd>
                </Box>
              )
            )
          }
        />
      </Box>

      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1.25,
          pb: 3,
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,255,255,0.14)",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(255,255,255,0.24)",
          },
        }}
      >
        {isLoading ? (
          <Box sx={{ px: 0.5 }}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={touch ? 48 : 42}
                sx={{ bgcolor: NAV.hover, borderRadius: "12px", mb: 0.5 }}
              />
            ))}
          </Box>
        ) : isSearching ? (
          results.length === 0 ? (
            <NoMatch query={query} py={4} />
          ) : (
            results.map((page) => (
              <Box
                key={`${page.groupId}-${page.id}`}
                component="a"
                href={page.link}
                onClick={(event: React.MouseEvent) => {
                  if (!isPlainClick(event)) return;
                  event.preventDefault();
                  handleNavigate(page);
                }}
                sx={{
                  ...rowSx({
                    height: touch ? NAV.rowHeightTouch : NAV.rowHeightDesktop,
                  }),
                  mb: 0.25,
                }}
              >
                <Box sx={{ display: "flex", flexShrink: 0 }}>
                  <NavIcon
                    id={page.id}
                    groupId={page.groupId}
                    size={18}
                    color="inherit"
                  />
                </Box>
                <PageLabel
                  title={page.label}
                  caption={page.groupLabel}
                  titleSize={touch ? "0.875rem" : "0.8125rem"}
                />
              </Box>
            ))
          )
        ) : (
          <LayoutGroup>
            {groups.map((group: NavGroup) => (
              <NavGroupSection
                key={group.id}
                group={group}
                isOpen={openGroups.includes(group.id)}
                activeLink={pathname}
                touch={touch}
                onToggle={handleToggle}
                onNavigate={handleNavigate}
              />
            ))}
          </LayoutGroup>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
